import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  api,
  useGetPosSeatsQuery,
  useGetFloorsQuery,
  useGetTablesQuery,
  useOpenPosSeatMutation,
} from "../../store/api";
import TableCard from "../../components/pos/TableCard";
import StatusBar from "../../components/pos/StatusBar";
import Modal from "../../components/common/Modal";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import LoadingSkeleton from "../../components/common/LoadingSkeleton";
import Toast from "../../components/common/Toast";
import OnScreenKeyboard from "../../components/common/OnScreenKeyboard";
import { usePosRealtime } from "../../hooks/usePosRealtime";
import { useStoreFeatures } from "../../hooks/useStoreFeatures";
import { useCustomerLookup } from "../../hooks/useCustomerLookup";
import { isChannelOrderingBlocked } from "../../utils/orderBlocking";
import {
  getPosErrorMessage,
  isOrderingBlockedError,
  ORDERING_BLOCKED_MESSAGE,
} from "../../utils/posErrorHandler";
import {
  sanitizeCustomerPhone,
  validateCustomerEmail,
  validateCustomerName,
  validateCustomerPhone,
} from "../../utils/customerValidation";

const TABLE_STATUS_REFRESH_INTERVAL_MS = 5000;

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getSeatGuestCount = (seat) => {
  // Support multiple backend field variants.
  const candidates = [
    seat?.covers,
    seat?.guestCount,
    seat?.Guests,
    seat?.guest_count,
    seat?.activeOrderGuestCount,
    seat?.activeOrderGuests,
    seat?.activeOrder?.guestCount,
    seat?.activeOrder?.covers,
    seat?.activeOrder?.guest_count,
  ];

  for (const candidate of candidates) {
    const value = toNumberOrNull(candidate);
    if (value !== null) return Math.max(0, Math.trunc(value));
  }

  return 0;
};

const getSeatCapacity = (seat) => {
  const candidates = [
    seat?.seatCapacity,
    seat?.capacity,
    seat?.tableCapacity,
    seat?.maxCovers,
  ];

  for (const candidate of candidates) {
    const value = toNumberOrNull(candidate);
    if (value !== null && value > 0) return Math.trunc(value);
  }

  return 4;
};

const getPendingCustomerStorageKey = (orderId) =>
  `pos.pendingCustomer.${orderId}`;

const DiningRoomControl = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedFloor, setSelectedFloor] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [toast, setToast] = useState(null);
  const [blockedModal, setBlockedModal] = useState({
    isOpen: false,
    title: "",
    message: "",
  });
  const [seatOpenedAtOverrides, setSeatOpenedAtOverrides] = useState({});
  const [pendingSeat, setPendingSeat] = useState(null);
  const [isOpeningSeat, setIsOpeningSeat] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerError, setCustomerError] = useState(null);
  const [customerErrorField, setCustomerErrorField] = useState(null);
  const [activeCustomerField, setActiveCustomerField] = useState("phone");
  const customerPhoneInputRef = useRef(null);
  const customerNameInputRef = useRef(null);
  const customerEmailInputRef = useRef(null);
  const customerLookup = useCustomerLookup({
    phone: customerPhone,
    enabled: showCustomerModal,
    minDigits: 10,
  });

  const [localSeats, setLocalSeats] = useState([]);
  const [activeSectionIds, setActiveSectionIds] = useState(new Set());
  const [sectionDirectory, setSectionDirectory] = useState({});
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const { features, isLoading: featuresLoading } = useStoreFeatures(true);
  const requireCustomerDetails = Boolean(
    features?.requireCustomerDetails ?? features?.RequireCustomerDetails,
  );
  const isOrderBlocked = isChannelOrderingBlocked(features, "POS");

  const getDeviceToken = useCallback(
    () =>
      localStorage.getItem("edgeDeviceToken") ||
      process.env.REACT_APP_DEVICE_TOKEN ||
      "",
    [],
  );

  const ensureDeviceToken = useCallback(() => {
    const token = getDeviceToken();
    if (token) return true;

    setBlockedModal({
      isOpen: true,
      title: "Device Not Paired",
      message:
        "This device is not paired. Please pair the device to continue.",
    });
    return false;
  }, [getDeviceToken]);

  // Real-time updates (no refetch, patch local state)
  usePosRealtime({
    autoInvalidate: false,
    onSeatStateChanged: (payload) => {
      if (!payload?.seatId) return;

      setLocalSeats((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const index = next.findIndex(
          (seat) => seat?.seatId === payload.seatId,
        );
        if (index === -1) return next;

        const current = next[index] || {};
        const nextSeat = {
          ...current,
          activeOrderId:
            payload.orderId === undefined ? current.activeOrderId : payload.orderId,
          activeOrderStatus:
            payload.orderStatus === undefined
              ? current.activeOrderStatus
              : payload.orderStatus,
          seatSessionOpenedAt:
            payload.seatSessionOpenedAt === undefined
              ? current.seatSessionOpenedAt
              : payload.seatSessionOpenedAt,
        };

        // If backend signals seat cleared, reset local status hints.
        if (!payload.orderId) {
          nextSeat.activeOrderId = null;
          nextSeat.activeOrderStatus = null;
          nextSeat.seatOrderStage = "NONE";
          nextSeat.status = "AVAILABLE";
        } else if (payload.orderStatus) {
          const normalizedStatus = String(payload.orderStatus).toUpperCase();
          nextSeat.seatOrderStage =
            normalizedStatus === "DRAFT" ? "DRAFT" : "IN_KITCHEN";
        }

        next[index] = nextSeat;
        return next;
      });

      if (payload.seatSessionOpenedAt !== undefined) {
        setSeatOpenedAtOverrides((prev) => {
          const next = { ...prev };
          if (payload.seatSessionOpenedAt) {
            next[payload.seatId] = payload.seatSessionOpenedAt;
          } else {
            delete next[payload.seatId];
          }
          return next;
        });
      }
    },
  });

  // Fetch seats from API
  const { data: seatsData } = useGetPosSeatsQuery(
    {
      floorId: selectedFloor !== "All" ? selectedFloor : undefined,
    },
    {
      pollingInterval: TABLE_STATUS_REFRESH_INTERVAL_MS,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );
  const {
    data: floorplanTables,
    isLoading: tablesLoading,
    error: tablesError,
    refetch: refetchTables,
  } = useGetTablesQuery(undefined, {
    pollingInterval: TABLE_STATUS_REFRESH_INTERVAL_MS,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Fetch floors for filter
  const { data: floorsData } = useGetFloorsQuery();

  // Open seat mutation
  const [openSeat] = useOpenPosSeatMutation();

  const seats = useMemo(() => {
    return Array.isArray(seatsData?.seats)
      ? seatsData.seats.filter((seat) => seat?.isActive !== false)
      : [];
  }, [seatsData?.seats]);
  const floors = useMemo(
    () =>
      Array.isArray(floorsData)
        ? floorsData.filter((floor) => floor?.isActive !== false)
        : [],
    [floorsData],
  );
  const activeFloorIdList = useMemo(
    () => floors.map((floor) => floor.floorId || floor.id).filter(Boolean),
    [floors],
  );
  const activeFloorIds = useMemo(
    () => new Set(activeFloorIdList),
    [activeFloorIdList],
  );

  useEffect(() => {
    let isActive = true;
    if (activeFloorIdList.length === 0) {
      setActiveSectionIds(new Set());
      setSectionsLoaded(true);
      return;
    }

    setSectionsLoaded(false);

    const subscriptions = activeFloorIdList.map((floorId) =>
      dispatch(api.endpoints.getSections.initiate(floorId, { forceRefetch: true })),
    );

    Promise.all(
      subscriptions.map((subscription) =>
        subscription
          .unwrap()
          .catch(() => []),
      ),
    )
      .then((results) => {
        if (!isActive) return;

        const nextSectionIds = new Set();
        const nextSectionDirectory = {};
        results.forEach((sections) => {
          if (!Array.isArray(sections)) return;
          sections
            .filter((section) => section?.isActive !== false)
            .forEach((section) => {
              const sectionId = section.sectionId || section.id;
              if (!sectionId) return;
              nextSectionIds.add(sectionId);
              nextSectionDirectory[sectionId] = section;
            });
        });

        setActiveSectionIds(nextSectionIds);
        setSectionDirectory(nextSectionDirectory);
        setSectionsLoaded(true);
      })
      .catch(() => {
        if (!isActive) return;
        setActiveSectionIds(new Set());
        setSectionDirectory({});
        setSectionsLoaded(true);
      });

    return () => {
      isActive = false;
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, [dispatch, activeFloorIdList]);

  useEffect(() => {
    if (!seatsData?.seats) return;
    setLocalSeats(seats);
  }, [seatsData?.seats, seats]);

  const openExistingSeatOrder = useCallback(async (seat) => {
    try {
      if (!ensureDeviceToken()) return;
      const result = await openSeat({
        seatId: seat.seatId,
        payload: {
          guestCount: 1,
          orderType: "DINEIN",
          channel: "POS",
          sourceSystem: "EDGE",
        },
      }).unwrap();

      if (result?.orderId) {
        if (result.seatId) {
          sessionStorage.setItem(
            `pos.orderSeatId.${result.orderId}`,
            result.seatId,
          );
        }
        navigate(`/pos/orders/${result.orderId}`, {
          state: { seatId: result.seatId },
        });
      }
      refetchTables();
    } catch (err) {
      setBlockedModal({
        isOpen: true,
        title: seat?.seatRef || "Table",
        message: getPosErrorMessage(err),
      });
    }
  }, [ensureDeviceToken, navigate, openSeat, refetchTables]);

  const resetCustomerForm = () => {
    setCustomerName("");
    setCustomerPhone("");
    setCustomerEmail("");
    setCustomerError(null);
    setCustomerErrorField(null);
  };

  useEffect(() => {
    if (!showCustomerModal) return;
    setActiveCustomerField("phone");
  }, [showCustomerModal]);

  useEffect(() => {
    if (!showCustomerModal) return;

    const input =
      activeCustomerField === "phone"
        ? customerPhoneInputRef.current
        : activeCustomerField === "name"
          ? customerNameInputRef.current
          : customerEmailInputRef.current;

    if (!input) return;

    const frame = window.requestAnimationFrame(() => {
      try {
        input.focus({ preventScroll: true });
      } catch {
        input.focus();
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeCustomerField, showCustomerModal]);

  useEffect(() => {
    if (!showCustomerModal) return;
    if (!customerLookup.customer) return;

    if (!customerName.trim() && customerLookup.customer.fullName) {
      setCustomerName(String(customerLookup.customer.fullName).trim());
    }
    if (!customerEmail.trim() && customerLookup.customer.email) {
      setCustomerEmail(String(customerLookup.customer.email).trim());
    }
  }, [showCustomerModal, customerLookup.customer, customerName, customerEmail]);

  const getActiveCustomerValue = () => {
    if (activeCustomerField === "phone") return customerPhone;
    if (activeCustomerField === "email") return customerEmail;
    return customerName;
  };

  const handleCustomerPhoneChange = (nextValue) => {
    const nextPhone = sanitizeCustomerPhone(nextValue);
    if (nextPhone !== customerPhone) {
      setCustomerName("");
      setCustomerEmail("");
    }
    setCustomerPhone(nextPhone);
    setCustomerError(null);
    setCustomerErrorField(null);
    if (nextPhone.length === 10) {
      setActiveCustomerField("name");
    }
  };

  const setActiveCustomerValue = (nextValue) => {
    if (activeCustomerField === "phone") {
      handleCustomerPhoneChange(nextValue);
      return;
    }
    if (activeCustomerField === "email") {
      setCustomerEmail(nextValue);
      return;
    }
    setCustomerName(nextValue);
  };

  const ensureValidPhoneBeforeAdvance = () => {
    const phoneError = validateCustomerPhone(customerPhone, {
      required: true,
    });
    if (phoneError) {
      setCustomerError(phoneError);
      setCustomerErrorField("phone");
      setActiveCustomerField("phone");
      return false;
    }
    return true;
  };

  const isCustomerPhoneValid = customerPhone.length === 10;
  const handleCustomerKeyboardDone = () => {
    if (activeCustomerField === "phone") {
      if (!ensureValidPhoneBeforeAdvance()) return;
      setActiveCustomerField("name");
      return;
    }
    if (activeCustomerField === "name") {
      setActiveCustomerField("email");
      return;
    }
    handleConfirmCustomerDetails();
  };

  const handleCloseCustomerModal = () => {
    if (isOpeningSeat) return;
    resetCustomerForm();
    setPendingSeat(null);
    setShowCustomerModal(false);
  };

  const openSeatWithTypeForSeat = useCallback(async (
    seat,
    orderType,
    customerDetails = null,
  ) => {
    if (!seat || isOpeningSeat) return;
    if (!ensureDeviceToken()) return;

    if (isOrderBlocked) {
      setToast({
        id: Date.now(),
        tone: "error",
        message: ORDERING_BLOCKED_MESSAGE,
      });
      return;
    }

    setIsOpeningSeat(true);
    setToast(null);

    try {
      const payload = {
        guestCount: 1,
        orderType,
        channel: "POS",
        sourceSystem: "EDGE",
      };

      if (customerDetails) {
        const { name, phone, email } = customerDetails;
        if (name) payload.customerName = name;
        if (phone) payload.customerPhone = phone;
        if (email) payload.customerEmail = email;
      }

      const result = await openSeat({
        seatId: seat.seatId,
        payload,
      }).unwrap();

      if (result?.orderId) {
        if (
          String(orderType).toUpperCase() === "TABLE_DINEIN" &&
          customerDetails &&
          (customerDetails.name || customerDetails.phone || customerDetails.email)
        ) {
          sessionStorage.setItem(
            getPendingCustomerStorageKey(result.orderId),
            JSON.stringify({
              name: customerDetails.name || "",
              phone: customerDetails.phone || "",
              email: customerDetails.email || "",
            }),
          );
        }
        if (String(orderType).toUpperCase() === "TAKEAWAY") {
          navigate(`/pos/takeaway/${result.orderId}`);
        } else {
          navigate(`/pos/orders/${result.orderId}`);
        }
      }
      refetchTables();
    } catch (err) {
      const errorCode =
        err?.data?.code || err?.data?.errorCode || err?.data?.title;
      if (errorCode === "CUSTOMER_DETAILS_REQUIRED") {
        setPendingSeat(seat);
        setShowCustomerModal(true);
        setCustomerError("Customer name, phone, and email are required.");
        setCustomerErrorField(null);
        return;
      }
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }
      setToast({
        id: Date.now(),
        tone: "error",
        message: getPosErrorMessage(err),
      });
    } finally {
      setIsOpeningSeat(false);
      setPendingSeat(null);
    }
  }, [
    dispatch,
    ensureDeviceToken,
    isOpeningSeat,
    isOrderBlocked,
    navigate,
    openSeat,
    refetchTables,
  ]);

  const handleSkipCustomerDetails = async () => {
    if (isOpeningSeat) return;

    const seatToOpen = pendingSeat;
    setShowCustomerModal(false);
    resetCustomerForm();

    if (seatToOpen) {
      await openSeatWithTypeForSeat(seatToOpen, "TABLE_DINEIN");
    }
  };

  const handleConfirmCustomerDetails = async () => {
    const name = customerName.trim();
    const phone = customerPhone.trim();
    const email = customerEmail.trim();

    const phoneError = validateCustomerPhone(phone, { required: true });
    if (phoneError) {
      setCustomerError(phoneError);
      setCustomerErrorField("phone");
      setActiveCustomerField("phone");
      return;
    }

    const nameError = validateCustomerName(name, { required: true });
    if (nameError) {
      setCustomerError(nameError);
      setCustomerErrorField("name");
      setActiveCustomerField("name");
      return;
    }

    const emailError = validateCustomerEmail(email, { required: false });
    if (emailError) {
      setCustomerError(emailError);
      setCustomerErrorField("email");
      setActiveCustomerField("email");
      return;
    }

    setCustomerError(null);
    setCustomerErrorField(null);
    setShowCustomerModal(false);

    if (pendingSeat) {
      await openSeatWithTypeForSeat(pendingSeat, "TABLE_DINEIN", {
        name,
        phone,
        email,
      });
    }
    resetCustomerForm();
  };

  const seatByTableId = useMemo(() => {
    const next = new Map();
    localSeats.forEach((seat) => {
      const tableId = seat?.tableId;
      if (tableId) next.set(String(tableId), seat);
    });
    return next;
  }, [localSeats]);

  const handleTableClick = useCallback(async (table) => {
    const seat = table?._seatData || null;

    if (table?.orderId) {
      if (seat?.seatId) {
        await openExistingSeatOrder(seat);
        return;
      }

      navigate(`/pos/orders/${table.orderId}`);
      return;
    }

    const tableStatus = String(table?.status || "").toUpperCase();
    if (tableStatus === "AVAILABLE" || tableStatus === "RESERVED") {
      setToast(null);

      if (!seat?.seatId) {
        setToast({
          id: Date.now(),
          tone: "error",
          message: "This table is not mapped to a POS seat yet.",
        });
        return;
      }

      if (isOrderBlocked) {
        setToast({
          id: Date.now(),
          tone: "error",
          message: ORDERING_BLOCKED_MESSAGE,
        });
        return;
      }

      if (requireCustomerDetails) {
        setPendingSeat(seat);
        setShowCustomerModal(true);
        setCustomerError(null);
        return;
      }

      await openSeatWithTypeForSeat(seat, "TABLE_DINEIN");
      return;
    }

    if (seat?.activeOrderId) {
      await openExistingSeatOrder(seat);
      return;
    }

    setBlockedModal({
      isOpen: true,
      title: table?.tableNo || "Table",
      message: `This table is currently ${table?.status || "unavailable"}.`,
    });
  }, [
    isOrderBlocked,
    navigate,
    openExistingSeatOrder,
    openSeatWithTypeForSeat,
    requireCustomerDetails,
  ]);

  const tables = useMemo(() => {
    const sourceTables = Array.isArray(floorplanTables) ? floorplanTables : [];

    return sourceTables
      .filter((table) => {
        if (table?.isActive === false) return false;

        const sectionId = table?.sectionId;
        const section = sectionId ? sectionDirectory[sectionId] : null;
        const sectionFloorId = section?.floorId || null;

        if (
          sectionsLoaded &&
          sectionId &&
          activeSectionIds.size > 0 &&
          !activeSectionIds.has(sectionId)
        ) {
          return false;
        }

        if (selectedFloor !== "All") {
          if (!sectionFloorId) return false;
          return String(sectionFloorId) === String(selectedFloor);
        }

        return (
          !sectionFloorId ||
          activeFloorIds.size === 0 ||
          activeFloorIds.has(sectionFloorId)
        );
      })
      .map((table) => {
        const seat = seatByTableId.get(String(table.tableId)) || null;

        return {
          tableId: table.tableId,
          tableNo: table.tableNo,
          displayName: table.displayName,
          seats: getSeatCapacity(seat),
          status: table.status,
          backgroundColor: table.backgroundColor,
          covers: getSeatGuestCount(seat),
          orderId: table.orderId,
          orderTotal: seat?.activeOrderGrandTotal || 0,
          orderTime:
            seat?.seatId
              ? seatOpenedAtOverrides[seat.seatId] || seat.seatSessionOpenedAt || null
              : null,
          _seatData: seat,
          onClick: () => handleTableClick({
            ...table,
            _seatData: seat,
          }),
        };
      });
  }, [
    activeFloorIds,
    activeSectionIds,
    floorplanTables,
    handleTableClick,
    seatByTableId,
    seatOpenedAtOverrides,
    sectionDirectory,
    sectionsLoaded,
    selectedFloor,
  ]);
  const filteredTables = useMemo(() => {
    if (selectedStatus === "all") return tables;
    return tables.filter(
      (table) => String(table.status || "").toUpperCase() === selectedStatus,
    );
  }, [tables, selectedStatus]);

  const stats = useMemo(() => {
    return tables.reduce((acc, table) => {
      const key = String(table.status || "").toUpperCase();
      if (key) {
        acc[key] = (acc[key] || 0) + 1;
      }
      return acc;
    }, {});
  }, [tables]);

  const isBoardLoading = tablesLoading || !sectionsLoaded || featuresLoading;

  return (
    <div className="min-h-screen">
      {toast && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onClose={() => setToast(null)}
          showClose={false}
        />
      )}
      {blockedModal.isOpen && (
        <Modal
          isOpen={blockedModal.isOpen}
          onClose={() =>
            setBlockedModal({ isOpen: false, title: "", message: "" })
          }
          title={`Table ${blockedModal.title}`}
          onConfirm={() =>
            setBlockedModal({ isOpen: false, title: "", message: "" })
          }
          confirmText="Okay"
          showCancel={false}
          size="sm"
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold">Table already opened</p>
                <p className="mt-1 text-sm text-rose-700/90 dark:text-rose-200/90">
                  {blockedModal.message}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Please wait for the table to become available or choose another table.
            </p>
          </div>
        </Modal>
      )}
      {isOpeningSeat ? (
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400" />
        </div>
      ) : (
      <>
      {/* Status Bar with Filters */}
      <StatusBar
        stats={stats}
        selectedFloor={selectedFloor}
        onFloorChange={setSelectedFloor}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        floors={floors}
        isLoading={isBoardLoading}
      />

      {isBoardLoading && (
        <div className="px-6 py-8">
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12">
            <LoadingSkeleton variant="table" count={20} />
          </div>
        </div>
      )}

      {/* Error State */}
      {tablesError && !isBoardLoading && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="inline-block rounded-full bg-red-100 dark:bg-red-900/30 p-4 mb-4">
              <svg
                className="h-12 w-12 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <p className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2">
              Failed to load tables
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {tablesError?.data?.message || "Please check your connection"}
            </p>
            <button
              onClick={() => refetchTables()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Table Grid with modern spacing */}
      {!isBoardLoading &&
        !tablesError &&
        !isOpeningSeat && (
        <div className="px-6 py-8">
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12">
            {filteredTables.map((table) => (
              <TableCard
                key={table.tableId}
                table={table}
                density="ultra-compact"
              />
            ))}
          </div>

          {/* Empty State with modern design */}
          {filteredTables.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 pos-animate-in">
              <div className="rounded-3xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 p-8 shadow-2xl">
                <svg
                  className="mb-6 h-20 w-20 text-slate-400 dark:text-slate-600 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
                <p className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2">
                  No seats found
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Try adjusting your filters to see more results
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={showCustomerModal}
        onClose={handleCloseCustomerModal}
        title="Customer Details"
        size="xl"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <Input
                ref={customerPhoneInputRef}
                label="Phone"
                required
                value={customerPhone}
                onChange={(e) => handleCustomerPhoneChange(e.target.value)}
                onFocus={() => setActiveCustomerField("phone")}
                placeholder="Enter 10 digits Phone Number"
                error={customerErrorField === "phone" ? customerError : null}
                className={
                  customerErrorField === "phone"
                    ? "ring-2 ring-rose-500 dark:ring-rose-400"
                    : activeCustomerField === "phone"
                      ? "ring-2 ring-indigo-500 dark:ring-indigo-400"
                    : ""
                }
              />
            </div>
              <Input
                ref={customerNameInputRef}
                label="Name"
                required
                value={customerName}
                disabled={!isCustomerPhoneValid}
              onChange={(e) => {
                setCustomerName(e.target.value);
                if (customerErrorField === "name") {
                  setCustomerError(null);
                  setCustomerErrorField(null);
                }
              }}
                onMouseDown={(e) => {
                  if (activeCustomerField !== "phone") return;
                  if (ensureValidPhoneBeforeAdvance()) return;
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onPointerDown={(e) => {
                  if (activeCustomerField !== "phone") return;
                  if (ensureValidPhoneBeforeAdvance()) return;
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onFocus={() => {
                  if (activeCustomerField === "phone" && !ensureValidPhoneBeforeAdvance()) {
                    return;
                  }
                  setActiveCustomerField("name");
                }}
                placeholder="John Doe"
                error={customerErrorField === "name" ? customerError : null}
                className={
                  activeCustomerField === "name"
                  ? "ring-2 ring-indigo-500 dark:ring-indigo-400"
                  : ""
              }
            />
              <Input
                ref={customerEmailInputRef}
                label="Email (Optional)"
                type="email"
              value={customerEmail}
              disabled={!isCustomerPhoneValid}
              onChange={(e) => {
                setCustomerEmail(e.target.value);
                if (customerErrorField === "email") {
                  setCustomerError(null);
                  setCustomerErrorField(null);
                }
              }}
              onMouseDown={(e) => {
                if (activeCustomerField !== "phone") return;
                if (ensureValidPhoneBeforeAdvance()) return;
                e.preventDefault();
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                if (activeCustomerField !== "phone") return;
                if (ensureValidPhoneBeforeAdvance()) return;
                e.preventDefault();
                e.stopPropagation();
              }}
              onFocus={() => {
                if (activeCustomerField === "phone" && !ensureValidPhoneBeforeAdvance()) {
                  return;
                }
                setActiveCustomerField("email");
              }}
              placeholder="john@demo.com"
              error={customerErrorField === "email" ? customerError : null}
              className={
                activeCustomerField === "email"
                  ? "ring-2 ring-indigo-500 dark:ring-indigo-400"
                : ""
              }
            />
          </div>
          {customerError && !customerErrorField && (
            <div className="text-xs text-rose-600 dark:text-rose-400">
              {customerError}
            </div>
          )}

          <OnScreenKeyboard
            layout={activeCustomerField === "phone" ? "numeric" : "alpha"}
            size={activeCustomerField === "phone" ? "lg" : "md"}
            value={getActiveCustomerValue()}
            onChange={setActiveCustomerValue}
            inputRef={
              activeCustomerField === "phone"
                ? customerPhoneInputRef
                : activeCustomerField === "name"
                  ? customerNameInputRef
                  : customerEmailInputRef
            }
            onDone={handleCustomerKeyboardDone}
            showDone={false}
            disabled={isOpeningSeat}
          />

          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleCloseCustomerModal}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSkipCustomerDetails}
              disabled={isOpeningSeat}
            >
              Skip
            </Button>
            <Button size="sm" onClick={handleConfirmCustomerDetails}>
              Continue
            </Button>
          </div>
        </div>
      </Modal>
      </>
      )}
    </div>
  );
};

export default DiningRoomControl;
